/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export type Campo = {
  cod_campo: string | number;
  des_campo: string;
  des_sinonimo?: string;
  flg_filtro: boolean;
  [key: string]: unknown;
};

type ScoredCampo = Campo & {
  score: number;
  highlightedName: string;
  highlightedSinonimo?: string;
};

const normalizeText = (raw: string): string =>
  (raw ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .replace(/\s+/g, ' ');

export const tokenize = (raw: string): string[] =>
  normalizeText(raw).split(' ').filter(Boolean);

const makeInitials = (raw: string): string =>
  tokenize(raw)
    .map((t) => t.charAt(0))
    .join('');

const trigrams = (raw: string): string[] => {
  const s = normalizeText(raw);
  return s.length < 3
    ? [s]
    : Array.from({ length: s.length - 2 }, (_, i) => s.slice(i, i + 3));
};

const jaccard = (a: string, b: string): number => {
  const A = trigrams(a);
  const B = trigrams(b);
  const setA = new Set(A);
  const setB = new Set(B);
  const inter = [...setA].filter((x) => setB.has(x)).length;
  const uni = new Set([...A, ...B]).size;
  return uni === 0 ? 0 : inter / uni;
};

const coverageScore = (queryTokens: string[], fieldTokens: string[]): number =>
  queryTokens.length === 0
    ? 0
    : queryTokens.reduce(
        (acc, qt) =>
          acc + (fieldTokens.some((ft) => ft.startsWith(qt)) ? 1 : 0),
        0,
      ) / queryTokens.length;

const phraseBoost = (fieldStr: string, q: string): number => {
  if (!q) return 0;
  const f = normalizeText(fieldStr);
  const idx = f.indexOf(q);
  if (idx < 0) return 0;
  return idx === 0 ? 1 : 0.6;
};

const initialsBoost = (fieldStr: string, qConcat: string): number => {
  if (!qConcat) return 0;
  const initials = makeInitials(fieldStr);
  if (!initials) return 0;
  if (initials.startsWith(qConcat)) return 1;
  return initials.includes(qConcat) ? 0.7 : 0;
};

const stripDiacritics = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const isAsciiAlnum = (c: string) => /[a-z0-9]/i.test(c);

type IndexedChar = { c: string; i: number };

const buildNormalizationMap = (original: string) => {
  const raw = original ?? '';
  const lower = raw.toLowerCase();

  const pieces: IndexedChar[] = Array.from(lower).flatMap((ch, i) => {
    const n = stripDiacritics(ch);

    if (!n) return [{ c: '', i }];
    return n.split('').map((c) => ({ c, i }));
  });

  const alnumOrSpace: IndexedChar[] = pieces.map(({ c, i }) => ({
    c: isAsciiAlnum(c) ? c : c ? ' ' : ' ',
    i,
  }));

  const collapsed: IndexedChar[] = alnumOrSpace.reduce<IndexedChar[]>(
    (acc, cur) => {
      if (!acc.length) return cur.c === ' ' ? acc : acc.concat(cur);
      const prev = acc[acc.length - 1];
      if (prev.c === ' ' && cur.c === ' ') return acc;
      return acc.concat(cur);
    },
    [],
  );

  const trimmed = collapsed.filter((_, idx, arr) => {
    if (idx === 0 && arr[idx].c === ' ') return false;
    if (idx === arr.length - 1 && arr[idx].c === ' ') return false;
    return true;
  });

  const normalizedJoin = trimmed.map((x) => x.c).join('');
  const normToOrigIndex = trimmed.map((x) => x.i);

  return { normToOrigIndex, normalizedJoin };
};

const highlight = (original: string, queryTokens: string[]): string => {
  if (!original || queryTokens.length === 0) return original;

  const { normToOrigIndex, normalizedJoin } = buildNormalizationMap(original);
  const tokens = [...new Set(queryTokens.filter(Boolean))].sort(
    (a, b) => b.length - a.length,
  );
  if (!tokens.length) return original;

  const ranges = tokens
    .map((t) =>
      Array.from(
        normalizedJoin.matchAll(
          new RegExp(t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
        ),
      ).map((m) => ({ start: m.index ?? 0, end: (m.index ?? 0) + t.length })),
    )
    .flat();

  if (!ranges.length) return original;

  const originalRanges = ranges
    .map(({ start, end }) => {
      const s = normToOrigIndex[start];
      const e = normToOrigIndex[Math.max(end - 1, start)] + 1;
      return s != null && e != null ? { start: s, end: e } : null;
    })
    .filter((x): x is { start: number; end: number } => !!x)
    // junta sobreposições
    .sort((a, b) => a.start - b.start)
    .reduce<{ start: number; end: number }[]>((acc, cur) => {
      if (!acc.length) return [cur];
      const last = acc[acc.length - 1];
      if (cur.start <= last.end) {
        // merge
        const merged = { start: last.start, end: Math.max(last.end, cur.end) };
        return acc.slice(0, -1).concat(merged);
      }
      return acc.concat(cur);
    }, []);

  const out = originalRanges.reduce((acc, { start, end }, idx) => {
    const prevEnd = idx === 0 ? 0 : originalRanges[idx - 1].end;
    return `${acc + original.slice(prevEnd, start)}<mark>${original.slice(
      start,
      end,
    )}</mark>${idx === originalRanges.length - 1 ? original.slice(end) : ''}`;
  }, '');

  return out || original;
};

/**
 * Calcula score de relevância combinando vários sinais.
 * Mantém tudo funcional (sem for...of).
 */
export const scoreCampo = (
  campo: Campo,
  query: string,
  qTokens: string[],
  qConcat: string,
): ScoredCampo => {
  const fullStr = `${campo.des_campo ?? ''} ${campo.des_sinonimo ?? ''}`.trim();
  const fTokens = tokenize(fullStr);

  const sCoverage = coverageScore(qTokens, fTokens); // 0..1
  const sPhrase = phraseBoost(fullStr, normalizeText(query)); // 0..1
  const sInitials = initialsBoost(fullStr, qConcat); // 0..1
  const sJaccard = jaccard(fullStr, query); // 0..1

  // Combinação ponderada (ajuste fino conforme feedback real)
  const score =
    0.36 * sCoverage + 0.26 * sJaccard + 0.22 * sPhrase + 0.16 * sInitials;

  return {
    ...campo,
    score: Number.isFinite(score) ? score : 0,
    highlightedName: highlight(campo.des_campo ?? '', qTokens),
    highlightedSinonimo: campo.des_sinonimo
      ? highlight(String(campo.des_sinonimo), qTokens)
      : undefined,
  };
};
